import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { CondoTask } from "types/CondoTask";

interface TasksContextType {
  tasks: CondoTask[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  tasksCount: number;
  selectedTaskId: number | null;
  setSelectedTaskId: (taskId: number | null) => void;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export function TasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<CondoTask[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  const fetchTasks = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tasks = await fetchWithCredentials(
        `/manager-api/tasks/?condo=${condoId}`
      );
      setTasks(tasks);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchTasks(condoId);
    },
    [fetchTasks]
  );

  const tasksCount = useMemo(() => tasks.length, [tasks]);

  return (
    <TasksContext.Provider
      value={{
        tasks,
        loading,
        error,
        refresh,
        tasksCount,
        selectedTaskId,
        setSelectedTaskId,
      }}
    >
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error("useTasks must be used within a TasksProvider");
  }
  return context;
}
